import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminDeviceAction, adminGetUserDevices } from '../../utils/remote';
import type { DeviceAction, DeviceActionResult, UserDevicesDetail } from '../../types/admin';
import { Badge, Button, ConfirmDialog, ErrorPanel, LoadingState, Modal } from '../../school_panel/components/ui';
import { KIND_LABEL } from './accountSignalKinds';
import { lastSeen } from '../../school_panel/pages/dashboards/sections/format';

interface Props {
  userId: number | null;
  onClose: () => void;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const EVENT_LABEL: Record<string, string> = {
  bind: 'Phone registered', login: 'Signed in', switch: 'Moved to a new phone', switch_confirm: 'Confirmed a phone change',
  switch_refused: 'Phone change refused (limit)', otp_sent: 'SMS code sent', otp_failed: 'Wrong SMS code', otp_ok: 'SMS code accepted',
  legacy_login_refused: 'Old app refused', grace_upload: 'Uploaded results after sign-out', content_reset: 'Content key reset',
  admin_reset: 'Admin reset phone changes', admin_signout: 'Admin signed out all phones', admin_bypass: 'Admin allowed a new phone',
  admin_move_downloads: 'Admin moved downloads', logout: 'Signed out', no_phone_for_code: 'No phone number for SMS code',
};
const REVOKE_LABEL: Record<string, string> = { switched: 'moved to a new phone', logout: 'signed out', admin: 'signed out by an admin' };
const humanize = (s: string) => { const t = s.replace(/_/g, ' '); return t.charAt(0).toUpperCase() + t.slice(1); };
const eventLabel = (k: string) => EVENT_LABEL[k] ?? humanize(k);
const revokeLabel = (r: string) => REVOKE_LABEL[r] ?? r.replace(/_/g, ' ');

/** Readable "key: value" pairs; actor ids are not shown. */
const detailPairs = (d: unknown): string => {
  if (d === null || d === undefined || d === '') return '';
  if (typeof d !== 'object') return String(d);
  return Object.entries(d as Record<string, unknown>)
    .filter(([k, v]) => k !== 'actor' && v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${humanize(k).toLowerCase()}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
};

type ActionCopy = { label: string; title: (n: string) => string; body: (n: string, d: UserDevicesDetail) => string; done: (r: DeviceActionResult) => string };

const ACTIONS: Record<DeviceAction, ActionCopy> = {
  'reset-switches': {
    label: 'Reset phone changes',
    title: (n) => `Reset ${n}'s phone changes?`,
    body: (n, d) => `${n} will get all ${d.switch_limit} phone changes back for the next ${d.window_days} days. Their current phone stays signed in.`,
    done: () => 'Phone changes reset.',
  },
  'sign-out-all': {
    label: 'Sign out all phones',
    title: (n) => `Sign out all of ${n}'s phones?`,
    body: (n) => `${n} will be signed out on every phone and will need to sign in again. The current phone stays the account's phone, so signing in there again needs no code. Results waiting to upload on a phone are kept.`,
    done: (r) => `Signed out ${r.revoked ?? 0} ${r.revoked === 1 ? 'session' : 'sessions'}.`,
  },
  'allow-new-device': {
    label: 'Allow a new phone',
    title: (n) => `Let ${n} sign in on a new phone?`,
    body: (n, d) => `Let ${n} sign in on one new phone in the next 24 hours without an SMS code. This does not lift the phone-change limit.${d.switches_used >= d.switch_limit ? ` ${n} has used all phone changes; also reset phone changes.` : ''}`,
    done: () => 'A new phone is allowed for 24 hours.',
  },
  'move-downloads': {
    label: 'Move downloads',
    title: (n) => `Move ${n}'s downloaded exams to the current phone?`,
    body: (n) => `Exams ${n} downloaded on other phones will count as downloaded on the phone that is signed in now.`,
    done: (r) => `Moved ${r.moved ?? 0} ${r.moved === 1 ? 'download' : 'downloads'}.`,
  },
};

const ORDER: DeviceAction[] = ['reset-switches', 'allow-new-device', 'move-downloads', 'sign-out-all'];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">{title}</h3>
      {children}
    </section>
  );
}

const AccountDevicesDrawer: React.FC<Props> = ({ userId, onClose }) => {
  const qc = useQueryClient();
  const [pending, setPending] = useState<DeviceAction | null>(null);
  const [msg, setMsg] = useState<{ text: string; error: boolean } | null>(null);

  const q = useQuery({
    queryKey: ['admin-user-devices', userId],
    queryFn: () => adminGetUserDevices(userId as number),
    enabled: userId !== null,
  });
  const d = q.data;

  const run = useMutation({
    mutationFn: (action: DeviceAction) => adminDeviceAction(userId as number, action),
    onSuccess: (res, action) => {
      qc.setQueryData(['admin-user-devices', userId], res.state);
      qc.invalidateQueries({ queryKey: ['admin-account-signals'] });
      setMsg({ text: ACTIONS[action].done(res), error: false });
      setPending(null);
    },
    onError: () => {
      setMsg({ text: 'That did not work. Nothing was changed; try again.', error: true });
      setPending(null);
    },
  });

  const name = d?.user.name ?? '';
  const who = firstName(name);

  return (
    <>
      <Modal isOpen={userId !== null} onClose={onClose} side="right" size="lg"
        eyebrow="Account sharing" title={d ? d.user.name : 'Devices'} subtitle={d ? `@${d.user.username}` : undefined}>
        {q.isLoading ? <LoadingState label="Loading devices…" />
          : q.isError || !d ? <ErrorPanel title="Failed to load" body="The devices could not be loaded." onRetry={() => q.refetch()} />
          : (
            <div>
              <Section title="Phone changes">
                <p className="text-sm text-ink">
                  <span className="font-mono font-semibold">{d.switches_used}</span> of <span className="font-mono font-semibold">{d.switch_limit}</span> used in the last {d.window_days} days.
                  {d.next_free_at && <> Next free on {fmtDate(d.next_free_at)}.</>}
                </p>
                {d.bypass_until && <p className="mt-1 text-sm text-muted">A new phone is allowed until {fmtDate(d.bypass_until)}.</p>}
              </Section>

              <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Device actions">
                {ORDER.map((a) => (
                  <Button key={a} size="sm" variant={a === 'sign-out-all' ? 'danger-outline' : 'ghost'} disabled={run.isPending}
                    onClick={() => { setMsg(null); setPending(a); }}>{ACTIONS[a].label}</Button>
                ))}
              </div>
              {msg && <p role={msg.error ? 'alert' : 'status'} className={`mb-4 text-sm ${msg.error ? 'text-danger-ink' : 'text-ink'}`}>{msg.text}</p>}

              <Section title="Devices">
                {d.devices.length === 0 ? <p className="text-sm text-muted">No devices yet.</p> : (
                  <ul className="m-0 list-none p-0">
                    {d.devices.map((v) => (
                      <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline py-2 text-[13px] last:border-b-0">
                        <span className="min-w-0">
                          <span className="font-semibold text-ink">{v.model || v.platform}</span>{' '}
                          <span className="text-muted">{v.platform} · v{v.app_version || '?'} · {v.protection}</span>
                          <span className="block font-mono text-xs text-muted">{v.last_ip_prefix || 'no IP'} · first {fmtDate(v.first_seen)} · last {lastSeen(v.last_seen)}</span>
                        </span>
                        {v.is_active && <Badge tone="success" size="sm">Active</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Recent sign-ins">
                {d.sessions.length === 0 ? <p className="text-sm text-muted">No sessions yet.</p> : (
                  <ul className="m-0 list-none p-0">
                    {d.sessions.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline py-2 text-[13px] last:border-b-0">
                        <span className="text-ink">Started {fmtDate(s.created)}
                          {s.revoked_at && <span className="text-muted"> · signed out {fmtDate(s.revoked_at)}{s.revoked_reason ? ` (${revokeLabel(s.revoked_reason)})` : ''}</span>}
                        </span>
                        {s.is_active && <Badge tone="success" size="sm">Current</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Flags">
                {d.signals.length === 0 ? <p className="text-sm text-muted">No flags.</p> : (
                  <ul className="m-0 list-none p-0">
                    {d.signals.map((s) => (
                      <li key={s.id} className="border-b border-hairline py-2 text-[13px] last:border-b-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <Badge tone={s.status === 'open' ? 'danger' : 'neutral'} size="sm">{s.status}</Badge>
                          <span className="font-semibold text-ink">{KIND_LABEL[s.kind] ?? s.kind}</span>
                          <span className="text-muted">seen {s.times_seen} {s.times_seen === 1 ? 'time' : 'times'}</span>
                        </span>
                        <span className="block text-muted">{s.summary}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Events">
                {d.events.length === 0 ? <p className="text-sm text-muted">No events yet.</p> : (
                  <ul className="m-0 list-none p-0">
                    {d.events.map((e) => (
                      <li key={e.id} className="border-b border-hairline py-2 text-[13px] last:border-b-0">
                        <span className="font-semibold text-ink">{eventLabel(e.kind)}</span>{' '}
                        <span className="font-mono text-xs text-muted">{fmtDate(e.created)}{e.ip_prefix ? ` · ${e.ip_prefix}` : ''}</span>
                        {detailPairs(e.detail) && <span className="block break-words text-xs text-muted">{detailPairs(e.detail)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          )}
      </Modal>

      <ConfirmDialog
        isOpen={pending !== null && !!d}
        title={pending && d ? ACTIONS[pending].title(who) : ''}
        body={pending && d ? ACTIONS[pending].body(who, d) : undefined}
        confirmLabel={pending ? ACTIONS[pending].label : 'Confirm'}
        danger={pending === 'sign-out-all'}
        busy={run.isPending}
        onConfirm={() => pending && run.mutate(pending)}
        onCancel={() => setPending(null)}
      />
    </>
  );
};

export default AccountDevicesDrawer;
